<template>
  <div>
    <DxPopup
      :visible="visible"
      @hiding="onHiding"
      @shown="popupShown"
      :width="'33%'"
      :height="'50%'"
      :show-title="true"
      :title="popupTitle"
      :drag-enabled="false"
      :close-on-outside-click="false"
      :show-close-button="true"
      :wrapperAttr="{ class: 'css-v2 gc-popup gc-popup-reorder-layout' }"
    >
      <div class="css-v2 gc-popup-form" ref="mainDiv">
        <keep-alive>
          <div class="sequence-tabs">
            <DxTabs
              :selected-index="selectedIndex"
              :items="getTabItems"
              :orientation="'horizontal'"
              :styling-mode="'secondary'"
              :keyExpr="'id'"
              :element-attr="{
                class: isAdminUser ? 'admin-user-tabs' : 'personal-user-tabs',
              }"
              :scrollByContent="true"
              :showNavButtons="true"
            />
            <div class="sequence-data">
              <span>
                <dx-data-grid
                  :show-borders="true"
                  :ref="'reorder-layout-grid'"
                  :columns="getColumns"
                  :data-source="getDataSource"
                  :columnChooser="{
                    enabled: false,
                  }"
                  :pager="{ visible: false }"
                  :row-alternation-enabled="true"
                  :height="getGridHeight"
                >
                  <DxSearchPanel :visible="false" />
                  <DxRowDragging
                    :allow-reordering="true"
                    :show-drag-icons="true"
                    :on-reorder="onRowReorder"
                  />
                </dx-data-grid>
              </span>
            </div>

            <div class="footer">
              <DxButton
                text="Cancel"
                type="normal"
                styling-mode="contained"
                :width="105"
                @click="onClickCancel"
              />
              <DxButton
                text="Save"
                type="default"
                styling-mode="contained"
                :width="105"
                @click="onClickSave"
              />
            </div>
          </div>
        </keep-alive>
      </div>
    </DxPopup>
  </div>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { mapGetters } from "vuex";
import { secureStorage } from "../../_services/storage";
import ArrayStore from "devextreme/data/array_store";
import ReportService from "../../_services/api/templates/report.service";
import { DxRowDragging } from "devextreme-vue/data-grid";

export default {
  name: "RenameGridLayoutPopup",
  components: {
    DxPopup,
    DxRowDragging,
  },
  props: {
    visible: {
      type: Boolean,
      default: false, // Default to false to ensure the popup is hidden when not explicitly shown
    },
    definitionKey: {
      type: String,
      required: true, // Ensure that definitionKey is always provided
    },
    showLoading: {
      type: Function,
      default: () => {}, // Default to an empty function to avoid errors if not provided
    },
    originalColumns: {
      type: Array,
      default: () => [], // Default to an empty array to avoid errors if not provided
    },
  },
  data() {
    return {
      popupTitle: "Set Layout Order",
      selectedIndex: 0,
      gridHeight: "auto",

      InstanceDataSource: new ArrayStore({
        key: "id",
        data: [],
      }),
      PersonalDataSource: new ArrayStore({
        key: "id",
        data: [],
      }),
    };
  },
  methods: {
    /*
     * onHiding: Reset the model and emit an event to update the visibility of the popup.
     * This method is called when the popup is being hidden, either by user action or programmatically.
     */
    onHiding() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.$emit("update:visible", false);
    },

    /*
     * popupShown: Fetch the necessary data when the popup is shown and adjust the height of the data grid.
     * This method is called when the popup is fully visible to the user.
     */
    async popupShown(e) {
      this.showLoading(true);
      if (this.isAdminUser) {
        this.selectedIndex = 0; // Default to "System Defaults" for admin users
        await this.getInstanceLevelSequence(); // Fetch instance level sequence for admin users
      } else {
        this.selectedIndex = 1; // Default to "My Defaults" for non-admin users
      }
      await this.getPersonalLevelSequence(); // Fetch personal level sequence for all users
      this.$nextTick(() => {
        this.$store.dispatch("hotKey/setHotKeyPopupMode", true);

        var content = e.component._$content;

        var popupHeight =
          content[0].querySelectorAll(".dx-popup-content")[0].clientHeight;

        var dataGridElement = content[0].querySelectorAll(
          ".dx-datagrid-rowsview",
        )[0];

        if (dataGridElement) {
          dataGridElement.style.height = `${popupHeight - 100}px`;

          this.gridHeight = `${popupHeight - 100}px`;
        }

        this.showLoading(false);
      });
    },

    /*
     * onClickSequenceTab: Update the selected index when a tab is clicked to switch between "System Defaults" and "My Defaults".
     * This method is called when a user clicks on one of the tabs to view either the instance level sequence or the personal level sequence.
     */
    onClickSequenceTab(e) {
      if (!e || !e.itemData) return;
      this.selectedIndex = e.itemData.id;
    },

    /*
     * getPersonalLevelSequence: Fetch the personal level sequence for the current user.
     * This method is called when the popup is shown .
     */
    async getPersonalLevelSequence() {
      this.PersonalDataSource.clear();
      const userDefinitions = this.getGridViewDefinitionByKey(
        this.definitionKey,
      );

      if (userDefinitions && userDefinitions.length > 0) {
        userDefinitions.forEach((element) => {
          this.PersonalDataSource.insert(element);
        });
      }
    },

    /*
     * getInstanceLevelSequence: Fetch the instance level sequence for the current user.
     * This method is called when the popup is shown & user is an admin.
     */
    async getInstanceLevelSequence() {
      // API call to get instance level sequence and update InstanceDataSource
      this.InstanceDataSource.clear();
      try {
        const response = await ReportService.getInstanceLevelSequence(
          this.definitionKey,
        );

        if (response && response.data && Array.isArray(response.data)) {
          response.data.forEach((element) => {
            this.InstanceDataSource.insert(element);
          });
        }
      } catch (error) {
        this.$store.dispatch("ui/setServerError", {
          error: error,
          title: "Failed",
        });
      }
    },

    /*
     * onRowReorder: Handle the reordering of rows in the data grid when a user drags and drops a row to a new position.
     * This method is called when a user finishes dragging and dropping a row in the data grid to reorder it.
     * @param {Object} e - The event object containing information about the row reorder action, including the item data, from index, and to index.
     */
    onRowReorder(e) {
      let itemData = e.itemData;

      const toIndex = e.toIndex;
      const fromIndex = e.fromIndex;

      const newTasks = [...this.getDataSource];

      newTasks.splice(fromIndex, 1);
      newTasks.splice(toIndex, 0, itemData);

      if (this.selectedIndex === 0) {
        this.InstanceDataSource._array = newTasks;
      } else if (this.selectedIndex === 1) {
        this.PersonalDataSource._array = newTasks;
      }

      this.$nextTick(() => {
        if (!this.getDataGridInstance) return;
        let instance = this.getDataGridInstance;
        if (instance) instance.refresh();
      });
    },

    /*
     * onClickCancel: Handle the click event for the "Cancel" button, prompting the user to confirm cancellation and resetting the model if confirmed.
     * This method is called when a user clicks the "Cancel" button in the popup to discard any changes made to the sequence order.
     */
    async onClickCancel() {
      const confirm = await this.$confirm(
        "Are you sure you want to cancel? All unsaved changes will be lost.",
        "Confirm Cancel",
      );

      if (confirm) {
        this.onHiding();
      }
    },

    /*
     * onClickSave: Handle the click event for the "Save" button, saving the new sequence order to the database and updating the store.
     * This method is called when a user clicks the "Save" button in the popup to save any changes made to the sequence order.
     */
    async onClickSave() {
      this.showLoading(true);

      try {
        await Promise.all([
          this.isAdminUser ? this.updateInstanceSequence() : Promise.resolve(),
          this.updatePersonalSequence(),
        ]);

        this.$notify("Sequence updated successfully!", "success");

        this.$nextTick(() => {
          this.onHiding();
        });
      } catch (error) {
        this.$store.dispatch("ui/setServerError", {
          error: error,
          title: "Failed",
        });
      } finally {
        this.showLoading(false);
      }
    },

    /*
     * updateInstanceSequence: Make an API call to update the instance level sequence based on the current order in InstanceDataSource.
     * This method is called when saving changes and the user is an admin, to persist the new sequence order for all users.
     */
    async updateInstanceSequence() {
      // API call to update instance level sequence based on InstanceDataSource
      const payload = this.InstanceDataSource._array.map((item, index) => ({
        RecordId: item.RecordID,
        Sequence: index + 1,
      }));

      try {
        await ReportService.updateInstanceLevelSequence(
          this.definitionKey,
          payload,
        );
      } catch (error) {
        throw error; // Let the error be caught in the onClickSave method
      }
    },

    /*
     * updatePersonalSequence: Make an API call to update the personal level sequence based on the current order in PersonalDataSource.
     * This method is called when saving changes, to persist the new sequence order for the current user.
     */
    async updatePersonalSequence() {
      const payload = this.PersonalDataSource._array.map((item, index) => ({
        RecordId: item.RecordID,
        Sequence: index + 1,
      }));

      try {
        await ReportService.updatePersonalLevelSequence(
          this.definitionKey,
          payload,
        );

        this.$store.dispatch("gridViewDefinition/updateDefinitionOrder", {
          key: this.definitionKey,
          definitionListOrdered: payload,
        });
      } catch (error) {
        throw error; // Let the error be caught in the onClickSave method
      }
    },
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    ...mapGetters({
      getGridViewDefinitionByKey:
        "gridViewDefinition/getGridViewDefinitionByKey",
    }),
    /* isAdminUser: Determine if the current user is an admin based on secure storage.
     * This computed property is used to conditionally render certain UI elements and determine which data to fetch and display.
     */
    isAdminUser() {
      return secureStorage.get("isAdmin");
    },

    /* getTabItems: Generate the tab items for the sequence tabs, showing "System Defaults" for admin users and "My Defaults" for all users.
     * This computed property is used to dynamically create the tabs based on the user's admin status.
     */
    getTabItems() {
      const items = [
        {
          id: 0,
          key: "everyone",
          text: "System Defaults",
          onClick: this.onClickSequenceTab,
          visible: this.isAdminUser, // Only show "System Defaults" tab for admin users
          cssClass: this.isAdminUser ? "admin-sequence-tab" : "", // Add a CSS class for admin users if needed
        },
        {
          id: 1,
          key: "me",
          text: "My Defaults",
          onClick: this.onClickSequenceTab,
          cssClass: !this.isAdminUser ? "personal-sequence-tab" : "", // Add a CSS class for non-admin users if needed
          class: !this.isAdminUser ? "personal-sequence-tab" : "", // Add a CSS class for non-admin users if needed
        },
      ];

      return items;
    },

    /* getGridHeight: Return the current height of the data grid, which is dynamically calculated based on the popup height.
     * This computed property is used to set the height of the data grid to ensure it fits within the popup without overflow.
     */
    getGridHeight() {
      return this.gridHeight;
    },

    /* getColumns: Define the columns for the data grid, including custom cell templates for displaying icons based on the template type.
     * This computed property is used to configure the columns of the data grid.
     */
    getColumns() {
      return [
        {
          dataField: "TemplateName",
          caption: "Column Name",
          cellTemplate: (container, options) => {
            container.innerHTML = `
              <div class="name-template">
                <div class="template-name">${options.data.TemplateName}</div>
                <div class="icon">
                  <span class="dx-icon ${
                    options.data.TemplateType === "Me"
                      ? " fa-solid fa-user"
                      : "fa-solid fa-users"
                  }"></span>
                </div>
              </div>
            `;
          },
        },
      ];
    },

    /* getDataSource: Return the data source for the data grid based on the selected tab.
     * This computed property is used to dynamically switch between instance and personal data sources.
     */
    getDataSource() {
      if (this.selectedIndex === 0) {
        return this.InstanceDataSource._array;
      } else if (this.selectedIndex === 1) {
        return this.PersonalDataSource._array;
      } else {
        return [];
      }
    },

    /* getDataGridInstance: Return the instance of the data grid component.
     * This computed property is used to access the data grid instance for performing operations like refreshing or updating data.
     */
    getDataGridInstance() {
      return this.$refs["reorder-layout-grid"].instance;
    },
  },

  watch: {
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          this.$nextTick(() => {
            this.onClickSave();
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-reorder-layout {
  .dx-layout-manager.dx-widget {
    padding-right: unset !important;
  }

  .gc-save-cancel-button-group {
    display: flex;
    justify-content: flex-end;

    .dx-field-item-content.dx-field-item-content-location-right {
      padding-top: 10px !important;
    }

    .dx-button-content {
      padding: 5px 5px !important;
    }

    .dx-button-text {
      font-size: 12px !important;
      padding: unset !important;
    }

    .gc-cancel-button {
      text-align: end;
      padding-right: 5px !important;
    }

    .gc-save-button {
      text-align: end !important;
      padding-left: 5px !important;
      .dx-button-submit-input {
        padding: unset !important;
      }

      .dx-button {
        background-color: #03a9f4;
        color: #fff !important;
        width: 70px !important;
      }
    }
  }

  .name-template {
    display: inline-flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;

    .fa-user::before,
    .fa-users::before {
      font-size: 12px;
      color: rgba(148, 148, 148, 0.87);
    }
  }

  .footer {
    padding-right: unset !important;
    gap: 15px;
    display: flex;
    justify-content: flex-end;
    margin-top: 12px !important;
  }

  .dx-datagrid-headers {
    display: none;
  }

  .dx-datagrid-rowsview {
    border-top: 1px solid #e0e0e0;
  }

  .personal-user-tabs {
    text-align: start !important;
  }
}
</style>
